import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import SendIcon from '@mui/icons-material/Send';
import ReactMarkdown from 'react-markdown';

const useBrowserLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

// History remains implemented, but its toolbar icon is hidden for now.
const SHOW_HISTORY_ICON = false;

export type CrossRulingLink = {
  title?: string | null;
  url: string;
  relevance_percent?: number | null;
  relevance_note?: string | null;
};

export type PublishClassificationUpdatePayload = {
  thread_id: string | null;
  stage:
    | 'REQUEST_SAVED'
    | 'CHAPTER_SUGGESTED'
    | 'GRAPH_CLARIFICATION'
    | 'GRAPH_PROGRESS'
    | 'FINAL_CLASSIFICATION'
    | 'FINAL_REVIEW';
  workflow_status?: string | null;
  workflow_stage?: string | null;

  chapter_number?: string | null;
  chapter_description?: string | null;

  current_node_id?: string | null;
  hts_code?: string | null;
  cmdt_desc?: string | null;

  message?: string | null;
  summary_text?: string | null;
  user_facing_message?: string | null;
  legal_basis_summary?: string | null;
  confidence_note?: string | null;
  pitfalls?: string | null;

  followup_question?: string | null;
  needs_options?: boolean | null;
  options?: string[];

  reasoning_trace?: string[];
  facts_relied_on?: string[];

  explanatory_notes?: string | null;
  section_chapter_notes?: string | null;
  cross_data_links?: CrossRulingLink[];
  clf_id?: string | null;
  use_structured_cross_links?: boolean | null;
};

export type PublishFinalClassificationPayload = {
  thread_id: string | null;
  stage: 'FINAL_CLASSIFICATION';
  workflow_status?: string | null;
  workflow_stage?: string | null;

  product_desc: string;
  material?: string | null;
  intended_use?: string | null;
  physical_state?: string | null;

  chapter_number?: string | null;
  chapter_description?: string | null;
  current_node_id?: string | null;

  hts_code: string;

  summary_text?: string | null;
  user_facing_message?: string | null;
  legal_basis_summary?: string | null;
  confidence_note?: string | null;

  reasoning_trace?: string[];
  facts_relied_on?: string[];

  cross_data_links?: CrossRulingLink[];
  cross_rulings_note?: string | null;
  use_structured_cross_links?: boolean | null;
};

export type HtsUiPatch = {
  stream_item: string;
  latest: {
    stage?: string | null;
    clf_id?: string | null;
    hts_code?: string | null;
    cmdt_desc?: string | null;
    explanatory_notes?: string | null;
    section_chapter_notes?: string | null;
    cross_data_links?: CrossRulingLink[];
    cross_rulings_note?: string | null;
    followup_items?: Array<{ question: string; answer?: string }>;
    options?: string[];
    user_facing_message?: string | null;
    summary_text?: string | null;
    reasoning_trace?: string[];
    facts_relied_on?: string[];
    legal_basis_summary?: string | null;
    confidence_note?: string | null;
    pitfalls?: string | null;
    chapter_number?: string | null;
    chapter_description?: string | null;
  };
};

type StartSessionResponse = {
  thread_id: string;
  workflow_stage: string;
  turn_status?: string;
  turn_id?: string | null;
};

type SendMessageResponse = {
  thread_id: string;
  workflow_stage: string;
  turn_status?: string;
  turn_id?: string | null;
};

type LiveStateResponse = {
  stream_items: string[];
  latest: HtsUiPatch['latest'];
  workflow_stage?: string | null;
  turn_status?: string;
  turn_id?: string | null;
  last_error?: string | null;
};

const TERMINAL_WORKFLOW_STAGES = new Set(['FINAL_CLASSIFICATION', 'STOPPED']);

type Props = {
  apiUrl: string; // PyAPI base URL, e.g. http://localhost:8002
  onThreadChange?: (threadId: string | null) => void;
  onNewChat?: () => void;
  onResponseStart?: () => void;
  onResponseEnd?: () => void;
  onError?: (message: string) => void;
  onLivePatch?: (patch: HtsUiPatch) => void;
  showToolbar?: boolean;
};

export type HtsClassificationChatHandle = {
  startNewChat: () => Promise<void>;
  getSaveSnapshot: () => HtsClassificationSaveSnapshot;
};

export type HtsObservedChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  createdAt: string;
  source: 'composer' | 'live_patch' | 'finalize';
};

export type HtsFinalReportSnapshot = {
  version: 1;
  source: 'publish_final_classification';
  threadId: string | null;
  savedAt?: string;
  payload: {
    thread_id?: string | null;
    product_desc?: string | null;
    hts_code?: string | null;
    cmdt_desc?: string | null;
    summary_text?: string | null;
    legal_basis_summary?: string | null;
    confidence_note?: string | null;
    reasoning_trace?: string[];
    facts_relied_on?: string[];
    chapter_number?: string | null;
    chapter_description?: string | null;
  };
  generated: Partial<HtsUiPatch['latest']>;
  streamItem: string;
};

export type HtsClassificationSaveSnapshot = {
  version: 1;
  source: 'hts-classification-managed-agent';
  threadId: string | null;
  capturedAt: string;
  observed: {
    messages: HtsObservedChatMessage[];
    streamItems: string[];
  };
  finalReport: HtsFinalReportSnapshot | null;
};

const buildObservedMessageId = () =>
  `hts-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const MAX_OBSERVED_MESSAGE_LENGTH = 4000;
const MAX_OBSERVED_MESSAGES = 100;
const MAX_REPORT_TEXT_LENGTH = 6000;
const MAX_STREAM_ITEM_LENGTH = 4000;
const MAX_STREAM_ITEMS = 100;
const MAX_ARRAY_ITEMS = 20;
const MAX_ARRAY_ITEM_LENGTH = 1000;

const truncateText = (
  value: unknown,
  maxLength: number,
): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength)}... [truncated]`;
};

const truncateNullableText = (
  value: unknown,
  maxLength: number,
): string | null | undefined => {
  if (value === null) {
    return null;
  }

  return truncateText(value, maxLength);
};

const limitStringArray = (values?: string[]): string[] | undefined => {
  if (!Array.isArray(values)) {
    return undefined;
  }

  return values
    .filter((value): value is string => typeof value === 'string')
    .slice(0, MAX_ARRAY_ITEMS)
    .map((value) => truncateText(value, MAX_ARRAY_ITEM_LENGTH) || '');
};

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  isLivePatch?: boolean;
  thoughtSeconds?: number | null;
  thoughtSteps?: ThoughtStep[];
};

export type ThoughtStep = {
  label: string;
  detail?: string;
};

export type StreamedResponseEnvelope = {
  message?: string | null;
  hts_code?: string | null;
  followup_items?: Array<{ question?: string | null; answer?: string | null }>;
  options?: string[];
};

// Deliberately excludes message text/content. Product descriptions can include
// confidential business details or sensitive substance names (see the
// content-policy handling elsewhere in this file); this list only carries what's
// needed to render the "recent classifications" sidebar and resume a thread —
// never the conversation content itself — since it's written to localStorage,
// which persists beyond the session and is readable by any script on this origin.
type SavedConversation = {
  id: string;
  threadId: string | null;
  title: string;
  updatedAt: number;
};

export const HTS_SUGGESTED_PROMPTS = [
  'Classify a laptop computer for office use',
  'Classify a stainless steel valve for fluid control',
  'Classify a porcelain teacup not sold in sets',
];

export const HTS_GREETING =
  "Hi - I'll help you classify your product under the USHTS. To start, please describe your product in as much detail as you can: what it is, what it's made of, how it works, and its intended use.";

const HTS_HISTORY_STORAGE_KEY = 'bite.htsClassification.chatHistory.v1';

export const HTS_THINKING_STEPS = [
  'Extracting and normalizing',
  'Reviewing tariff chapters',
  'Checking classification path',
  'Preparing follow-up options',
];

export const HTS_BASE_THOUGHT_STEPS: ThoughtStep[] = [
  { label: 'Checking workflow state assumptions' },
  {
    label: 'Extracting and normalizing product data',
    detail:
      'Preparing the product facts for the classification workflow and identifying missing details.',
  },
  { label: 'Determining classification chapter' },
  { label: 'Evaluating recommendation approach' },
  { label: 'Formulating user confirmation question' },
];

const HTS_FINAL_REVIEW_PROMPT = 'Which option do you choose?';

const HTS_FINAL_REVIEW_OPTIONS = [
  'Accept this result and give final report',
  'Add more product detail or explain your concerns and try again',
];

const isFinalReviewMenuText = (value?: string | null) => {
  const normalized = value?.toLowerCase() || '';
  return (
    normalized.includes('what would you like to do next') &&
    normalized.includes('accept this result')
  );
};

const isFinalReviewAcceptance = (
  value?: string | null,
  fromFinalReviewPrompt = false,
) => {
  const normalized = value?.trim().toLowerCase() || '';
  if (!normalized) {
    return false;
  }

  return (
    (fromFinalReviewPrompt && /^(option\s*)?1(\b|[.)])/.test(normalized)) ||
    normalized === 'accept' ||
    normalized.includes('accept this result') ||
    normalized.includes('give final report') ||
    normalized.includes('final report')
  );
};

export const formatThoughtDuration = (seconds: number | null): string => {
  if (!seconds || seconds < 2) {
    return 'Thought for a moment';
  }

  return `Thought for ${seconds}s`;
};

export const tryParseStreamEnvelope = (
  rawText: string,
): StreamedResponseEnvelope | null => {
  const trimmed = rawText.trim();
  if (!trimmed.startsWith('{')) {
    return null;
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    return parsed as StreamedResponseEnvelope;
  } catch (_error) {
    return null;
  }
};

export const isPlaceholderText = (value?: string | null): boolean => {
  const trimmed = value?.trim() || '';
  return /^<[^>]+>$/.test(trimmed);
};

export const cleanDisplayText = (value?: string | null): string => {
  const trimmed = value?.trim() || '';
  return isPlaceholderText(trimmed) ? '' : trimmed;
};

const formatValidationIssue = (issue: Record<string, unknown>) => {
  const location = Array.isArray(issue.loc)
    ? issue.loc.map(String).join('.')
    : typeof issue.loc === 'string'
    ? issue.loc
    : '';
  const message =
    typeof issue.msg === 'string' ? issue.msg : 'Invalid request payload.';

  return location ? `${location}: ${message}` : message;
};

export const toErrorMessage = (value: unknown, fallback: string): string => {
  if (typeof value === 'string') {
    return cleanDisplayText(value) || fallback;
  }

  if (Array.isArray(value)) {
    const messages = value
      .map((item) => toErrorMessage(item, ''))
      .filter(Boolean);
    return messages.length > 0 ? messages.join('; ') : fallback;
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;

    if ('msg' in record || 'loc' in record) {
      return formatValidationIssue(record);
    }

    const nested =
      record.message || record.detail || record.error || record.errors;
    if (nested) {
      return toErrorMessage(nested, fallback);
    }

    try {
      return JSON.stringify(record);
    } catch (_error) {
      return fallback;
    }
  }

  return fallback;
};

export const bulletList = (items: string[]): string =>
  items
    .map(cleanDisplayText)
    .filter(Boolean)
    .map((item) => `- ${item}`)
    .join('\n');

const normalizeOptions = (options?: string[] | null) =>
  (options || []).map(cleanDisplayText).filter(Boolean);

const extractOptionsFromText = (value?: string | null) => {
  const text = value || '';
  const optionsIndex = text.search(/(^|\n)\s*options\s*:/i);
  if (optionsIndex === -1) {
    return [];
  }

  const optionLines = text.slice(optionsIndex).split('\n').slice(1);
  const options: string[] = [];

  for (const line of optionLines) {
    const trimmed = line.trim();
    if (!trimmed) {
      if (options.length > 0) {
        break;
      }
      continue;
    }

    const optionMatch = trimmed.match(
      /^(?:[-*\u2022]\s+|\d+[.)]\s+|[A-Z][.)]\s+)(.+)$/,
    );
    if (!optionMatch) {
      if (options.length > 0) {
        break;
      }
      continue;
    }

    const option = cleanDisplayText(optionMatch[1]);
    if (option) {
      options.push(option);
    }
  }

  return options;
};

const hasOptionsBlock = (text: string) => /(^|\n)\s*options\s*:/i.test(text);

const formatOptionsBlock = (options: string[]) =>
  options.length > 0
    ? `Options:\n${options
        .map((option, index) => `${index + 1}. ${option}`)
        .join('\n')}`
    : '';

const getOpenFollowupQuestion = (
  items?: Array<{ question?: string | null; answer?: string | null }>,
) =>
  cleanDisplayText(
    items?.find((item) => item?.question && !item?.answer)?.question,
  );

export const buildFinalResultDisplayText = (patch: HtsUiPatch): string => {
  const latest = patch.latest || {};
  const htsCode = cleanDisplayText(latest.hts_code);
  const description = cleanDisplayText(latest.cmdt_desc);
  const summary =
    cleanDisplayText(latest.summary_text) ||
    cleanDisplayText(latest.user_facing_message);
  const legalBasis = cleanDisplayText(latest.legal_basis_summary);
  const confidence = cleanDisplayText(latest.confidence_note);
  const pitfalls = cleanDisplayText(latest.pitfalls);
  const facts = bulletList(latest.facts_relied_on || []);
  const reasoning = bulletList(latest.reasoning_trace || []);
  const parts = ['Proposed HTS Classification'];

  if (htsCode) {
    parts.push(`HTS: ${htsCode}`);
  }

  if (description) {
    parts.push(`Description: ${description}`);
  }

  if (summary) {
    parts.push(`Why this result:\n${summary}`);
  }

  if (legalBasis) {
    parts.push(`Legal basis:\n\n${legalBasis}`);
  } else if (reasoning) {
    parts.push(`Legal basis:\n\n${reasoning}`);
  }

  if (facts) {
    parts.push(`Facts relied on:\n\n${facts}`);
  }

  if (confidence) {
    parts.push(`Confidence note:\n${confidence}`);
  }

  if (pitfalls) {
    parts.push(`Things to note:\n${pitfalls}`);
  }

  return parts.length > 1 ? parts.join('\n\n').trim() : '';
};

// The official U.S. International Trade Commission tariff schedule site.
// Its search page accepts the code as a free-text query, dots and all, and
// resolves straight to the matching entry.
const HTSUS_SEARCH_BASE_URL = 'https://hts.usitc.gov/search?query=';

export const buildHtsusLookupUrl = (htsCode: string): string =>
  `${HTSUS_SEARCH_BASE_URL}${encodeURIComponent(htsCode)}`;

export const buildFinalClassificationText = (patch: HtsUiPatch): string => {
  const latest = patch.latest || {};
  const htsCode = cleanDisplayText(latest.hts_code);
  const description = cleanDisplayText(latest.cmdt_desc);
  const summary =
    cleanDisplayText(latest.summary_text) ||
    cleanDisplayText(latest.user_facing_message);
  const legalBasis = cleanDisplayText(latest.legal_basis_summary);
  const confidence = cleanDisplayText(latest.confidence_note);
  const facts = (latest.facts_relied_on || [])
    .map(cleanDisplayText)
    .filter((s) => s.length > 0);
  const reasoning = (latest.reasoning_trace || [])
    .map(cleanDisplayText)
    .filter((s) => s.length > 0);
  const crossLinks = latest.cross_data_links || [];

  if (!htsCode && !description) return '';

  const parts: string[] = [];

  parts.push(
    htsCode
      ? `## Final HTS Classification\n\n**${htsCode}** — [View on HTS.gov ↗](${buildHtsusLookupUrl(
          htsCode,
        )})`
      : '## Final HTS Classification',
  );

  if (description) {
    parts.push(`### Commodity Description\n\n${description}`);
  }

  if (summary) {
    parts.push(`### Summary\n\n${summary}`);
  }

  if (legalBasis) {
    parts.push(`### Legal Basis\n\n${legalBasis}`);
  }

  if (facts.length > 0) {
    parts.push(
      `### Facts Relied On\n\n${facts.map((f) => `- ${f}`).join('\n')}`,
    );
  }

  if (reasoning.length > 0) {
    parts.push(
      `### Reasoning Trace\n\n${reasoning.map((r) => `- ${r}`).join('\n')}`,
    );
  }

  if (confidence) {
    parts.push(`**Confidence Note:** ${confidence}`);
  }

  if (crossLinks.length > 0) {
    const linkItems = crossLinks
      .map((link) => {
        const title = cleanDisplayText(link.title) || 'Ruling';
        const pct =
          typeof link.relevance_percent === 'number'
            ? ` — Relevance: **${Math.round(link.relevance_percent)}%**`
            : '';
        const note = cleanDisplayText(link.relevance_note);
        return note
          ? `- **${title}**${pct}\n  ${note}`
          : `- **${title}**${pct}`;
      })
      .join('\n\n');
    parts.push(`### Relevant CROSS Rulings\n\n${linkItems}`);
  }

  return parts.join('\n\n');
};

export const buildPatchDisplayText = (
  patch: HtsUiPatch,
  optionsOverride: { suppressFinalReviewMenu?: boolean } = {},
): string => {
  const stage = patch.latest?.stage || '';
  const rawText = patch?.stream_item?.trim() || '';
  const envelope = tryParseStreamEnvelope(rawText);

  if (
    !envelope &&
    stage !== 'FINAL_REVIEW' &&
    stage !== 'FINAL_CLASSIFICATION'
  ) {
    return cleanDisplayText(rawText);
  }

  const message = cleanDisplayText(envelope?.message);
  const followupQuestion =
    getOpenFollowupQuestion(patch.latest?.followup_items) ||
    getOpenFollowupQuestion(envelope?.followup_items);
  const latestOptions = normalizeOptions(patch.latest?.options);
  const envelopeOptions = normalizeOptions(envelope?.options);
  const options =
    latestOptions.length > 0
      ? latestOptions
      : envelopeOptions.length > 0
      ? envelopeOptions
      : extractOptionsFromText(message);

  if (stage === 'FINAL_CLASSIFICATION') {
    const finalText = buildFinalClassificationText(patch);
    if (finalText) return finalText;
  }

  if (stage === 'FINAL_REVIEW') {
    // Use the backend-authored envelope.message (rich review content) as the base,
    // falling back to a structured build from latest fields if absent.
    const baseText =
      cleanDisplayText(envelope?.message) || buildFinalResultDisplayText(patch);
    if (baseText) {
      // Fixed, UI-authored — guaranteed to appear regardless of how the
      // model phrased its own summary, so "not final yet" is never missed.
      const finalParts = [
        '**⏳ This is a proposed classification — nothing is final until you confirm below.**',
        baseText,
      ];
      const shouldShowReviewMenu = !optionsOverride.suppressFinalReviewMenu;
      const reviewQuestion = shouldShowReviewMenu
        ? followupQuestion || HTS_FINAL_REVIEW_PROMPT
        : followupQuestion;
      const reviewOptions =
        shouldShowReviewMenu && options.length === 0
          ? HTS_FINAL_REVIEW_OPTIONS
          : shouldShowReviewMenu
          ? options
          : [];

      // Only add "What would you like to do next?" if not already in baseText
      if (
        reviewOptions.length > 0 &&
        !/what would you like to do next\?/i.test(baseText)
      ) {
        finalParts.push('What would you like to do next?');
      }

      if (reviewOptions.length > 0 && !hasOptionsBlock(baseText)) {
        finalParts.push(formatOptionsBlock(reviewOptions));
      }

      // Only add reviewQuestion if it isn't already present in the assembled parts
      const assembled = finalParts.join('\n\n');
      if (
        reviewQuestion &&
        !/what would you like to do next\?/i.test(assembled)
      ) {
        finalParts.push(reviewQuestion);
      }

      return finalParts.join('\n\n').trim();
    }
  }

  if (!envelope) {
    return cleanDisplayText(rawText);
  }

  const parts = [];

  if (message) {
    parts.push(message);
  }

  if (followupQuestion && !message.includes(followupQuestion)) {
    parts.push(followupQuestion);
  }

  if (options.length > 0 && !hasOptionsBlock(message)) {
    parts.push(formatOptionsBlock(options));
  }

  return parts.join('\n\n').trim();
};

export const buildPatchThoughtSteps = (patch: HtsUiPatch): ThoughtStep[] => {
  const stage = patch.latest?.stage || '';
  const steps = HTS_BASE_THOUGHT_STEPS.slice(0, 1);

  if (stage || patch.latest?.chapter_number || patch.latest?.hts_code) {
    steps.push(HTS_BASE_THOUGHT_STEPS[1]);
  }

  if (patch.latest?.chapter_number || stage.includes('CHAPTER')) {
    steps.push(HTS_BASE_THOUGHT_STEPS[2]);
  }

  if (patch.latest?.options?.length || patch.latest?.followup_items?.length) {
    steps.push(HTS_BASE_THOUGHT_STEPS[3], HTS_BASE_THOUGHT_STEPS[4]);
  }

  // Backend note that it's acting on an HTS code the user supplied (hint
  // intake, heading anchor, dispute pin/converge) — surface it in the audit
  // trace too, not just as the chat message itself. Fixed label so
  // mergeThoughtSteps' dedupe-by-label keeps exactly one per message.
  if (stage === 'REASONING_NOTE') {
    const noteDetail = patch.latest?.reasoning_trace?.[0];
    steps.push({
      label: 'Weighing your input against the tariff text',
      detail: noteDetail,
    });
  }

  return steps;
};

const mergeThoughtSteps = (
  current: ThoughtStep[] | undefined,
  next: ThoughtStep[],
) => {
  const merged = [...(current || [])];
  next.forEach((step) => {
    if (!merged.some((item) => item.label === step.label)) {
      merged.push(step);
    }
  });
  return merged;
};

export const buildConversationTitle = (text: string): string => {
  const normalized = text.trim().replace(/\s+/g, ' ');
  if (!normalized) {
    return 'New Classification';
  }

  if (normalized.length <= 44) {
    return normalized;
  }

  return `${normalized.slice(0, 41)}...`;
};

const createConversationId = () =>
  `hts_conversation_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const coerceTimestamp = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return Date.now();
};

const readStoredConversations = (): SavedConversation[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(HTS_HISTORY_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .filter((item) => item && typeof item.id === 'string')
      .map((item) => ({
        id: item.id,
        threadId:
          typeof item.threadId === 'string' && item.threadId
            ? item.threadId
            : null,
        title:
          typeof item.title === 'string' && item.title
            ? item.title
            : 'New Classification',
        updatedAt: coerceTimestamp(item.updatedAt),
      }))
      .sort((a, b) => b.updatedAt - a.updatedAt);
  } catch (_error) {
    return [];
  }
};

const writeStoredConversations = (items: SavedConversation[]) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(HTS_HISTORY_STORAGE_KEY, JSON.stringify(items));
};

const groupConversationsByAge = (items: SavedConversation[]) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const oneDay = 24 * 60 * 60 * 1000;
  const groups = [
    { label: 'Today', items: [] as SavedConversation[] },
    { label: 'Yesterday', items: [] as SavedConversation[] },
    { label: 'Last 7 days', items: [] as SavedConversation[] },
    { label: 'Last 30 days', items: [] as SavedConversation[] },
    { label: 'Older', items: [] as SavedConversation[] },
  ];

  items.forEach((item) => {
    const itemDate = new Date(item.updatedAt);
    itemDate.setHours(0, 0, 0, 0);
    const dayDiff = Math.floor((today.getTime() - itemDate.getTime()) / oneDay);

    if (dayDiff <= 0) {
      groups[0].items.push(item);
    } else if (dayDiff === 1) {
      groups[1].items.push(item);
    } else if (dayDiff < 7) {
      groups[2].items.push(item);
    } else if (dayDiff < 30) {
      groups[3].items.push(item);
    } else {
      groups[4].items.push(item);
    }
  });

  return groups.filter((group) => group.items.length > 0);
};

const HtsClassificationChat = (
  {
    apiUrl,
    onThreadChange,
    onNewChat,
    onResponseStart,
    onResponseEnd,
    onError,
    onLivePatch,
    showToolbar = true,
  }: Props,
  ref: React.ForwardedRef<HtsClassificationChatHandle>,
) => {
  const pyApiClient = useMemo(
    () =>
      axios.create({
        baseURL: apiUrl,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    [apiUrl],
  );

  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workflowStage, setWorkflowStage] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [composerValue, setComposerValue] = useState('');
  const [conversationTitle, setConversationTitle] =
    useState('New Classification');
  const [activeConversationId, setActiveConversationId] = useState(() =>
    createConversationId(),
  );
  const [savedConversations, setSavedConversations] = useState<
    SavedConversation[]
  >(() => readStoredConversations());
  const [thinkingStartedAt, setThinkingStartedAt] = useState<number | null>(
    null,
  );
  const [thinkingElapsedSeconds, setThinkingElapsedSeconds] = useState(0);
  const [expandedThoughtIds, setExpandedThoughtIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [historyOpen, setHistoryOpen] = useState(false);

  const threadIdRef = useRef<string | null>(null);
  const lastSeenStreamCountRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const chatRootRef = useRef<HTMLDivElement | null>(null);
  const composerFormRef = useRef<HTMLFormElement | null>(null);
  const composerInputRef = useRef<HTMLTextAreaElement | null>(null);
  const previousMessageCountRef = useRef(0);
  const wasSendingRef = useRef(false);
  const thinkingStartedAtRef = useRef<number | null>(null);
  const lastSubmittedUserMessageRef = useRef('');
  const lastSubmittedFromFinalReviewPromptRef = useRef(false);
  const productDescRef = useRef<string | null>(null);
  const observedMessagesRef = useRef<HtsObservedChatMessage[]>([]);
  const observedStreamItemsRef = useRef<string[]>([]);
  const finalReportSnapshotRef = useRef<HtsFinalReportSnapshot | null>(null);
  const onThreadChangeRef = useRef<Props['onThreadChange']>(onThreadChange);
  const onNewChatRef = useRef<Props['onNewChat']>(onNewChat);
  const onResponseStartRef = useRef<Props['onResponseStart']>(onResponseStart);
  const onResponseEndRef = useRef<Props['onResponseEnd']>(onResponseEnd);
  const onErrorRef = useRef<Props['onError']>(onError);
  const onLivePatchRef = useRef<Props['onLivePatch']>(onLivePatch);
  const streamingIntervalRef = useRef<number | null>(null);

  const stopStreaming = () => {
    if (streamingIntervalRef.current !== null) {
      window.clearInterval(streamingIntervalRef.current);
      streamingIntervalRef.current = null;
    }
  };

  useEffect(() => stopStreaming, []);

  useEffect(() => {
    onThreadChangeRef.current = onThreadChange;
  }, [onThreadChange]);

  useEffect(() => {
    onNewChatRef.current = onNewChat;
  }, [onNewChat]);

  useEffect(() => {
    onResponseStartRef.current = onResponseStart;
  }, [onResponseStart]);

  useEffect(() => {
    onResponseEndRef.current = onResponseEnd;
  }, [onResponseEnd]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    onLivePatchRef.current = onLivePatch;
  }, [onLivePatch]);

  useBrowserLayoutEffect(() => {
    const input = composerInputRef.current;
    if (!input) return;

    input.style.height = '34px';

    const computedMaxHeight = Number.parseFloat(
      window.getComputedStyle(input).maxHeight,
    );
    const maxHeight = Number.isFinite(computedMaxHeight)
      ? computedMaxHeight
      : 120;
    const nextHeight = Math.min(input.scrollHeight, maxHeight);

    input.style.height = `${Math.max(34, nextHeight)}px`;
    input.style.overflowY =
      input.scrollHeight > maxHeight + 1 ? 'auto' : 'hidden';
  }, [composerValue]);

  useEffect(() => {
    const chatRoot = chatRootRef.current;
    const composerForm = composerFormRef.current;
    if (!chatRoot || !composerForm) return undefined;

    const updateComposerHeight = () => {
      chatRoot.style.setProperty(
        '--bite-hts-composer-height',
        `${Math.ceil(composerForm.getBoundingClientRect().height)}px`,
      );
    };

    updateComposerHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateComposerHeight);
      return () => window.removeEventListener('resize', updateComposerHeight);
    }

    const resizeObserver = new ResizeObserver(updateComposerHeight);
    resizeObserver.observe(composerForm);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (messages.length === 0) {
      return;
    }

    setSavedConversations((current) => {
      const nextItem: SavedConversation = {
        id: activeConversationId,
        threadId: threadIdRef.current,
        title: conversationTitle,
        updatedAt: Date.now(),
      };
      const next = [
        nextItem,
        ...current.filter((item) => item.id !== activeConversationId),
      ].sort((a, b) => b.updatedAt - a.updatedAt);

      writeStoredConversations(next);
      return next;
    });
  }, [activeConversationId, conversationTitle, messages]);

  // Scroll to the bottom when a genuinely new message is appended, or when a
  // turn that was narrating/streaming into an EXISTING message (see
  // appendLivePatchMessage — narration text and the final classification's
  // token-by-token reveal both mutate the same message in place) just
  // finished — never while it's still going, so a reply streaming in never
  // yanks the view back down while someone reads an earlier message. Without
  // the `sending` edge, a long narration trail growing the transcript well
  // past the last scroll position would leave the finished answer off-screen
  // with nothing left to bring it back into view.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const isNewMessage = messages.length !== previousMessageCountRef.current;
    previousMessageCountRef.current = messages.length;

    const justFinishedSending = wasSendingRef.current && !sending;
    wasSendingRef.current = sending;

    if (!isNewMessage && !justFinishedSending) return;

    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  useEffect(() => {
    if (!thinkingStartedAt) {
      setThinkingElapsedSeconds(0);
      return undefined;
    }

    const updateElapsed = () => {
      setThinkingElapsedSeconds(
        Math.max(0, Math.floor((Date.now() - thinkingStartedAt) / 1000)),
      );
    };

    updateElapsed();
    const id = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(id);
  }, [thinkingStartedAt]);

  const appendLivePatchMessage = useCallback(
    (patch: HtsUiPatch, responseId: string) => {
      const text = buildPatchDisplayText(patch, {
        suppressFinalReviewMenu: isFinalReviewAcceptance(
          lastSubmittedUserMessageRef.current,
          lastSubmittedFromFinalReviewPromptRef.current,
        ),
      });
      if (!text) return;

      const stage = patch.latest?.stage || '';
      const thoughtSteps = buildPatchThoughtSteps(patch);
      const replaceStages = new Set([
        'CHAPTER_CONFIRMED',
        'GRAPH_PROGRESS',
        'FINAL_CLASSIFICATION',
        'FINAL_CLASSIFICATION_ACCEPTED',
        'FINAL_REVIEW',
      ]);
      // For FINAL_CLASSIFICATION, place an empty message then stream the text in.
      const isFinalClass = stage === 'FINAL_CLASSIFICATION';
      const initialText = isFinalClass ? '' : text;

      setMessages((prev) => {
        // If this response already has a message (even from a batched sibling updater),
        // update it in-place rather than creating a second one.
        const existingIndex = prev.findIndex((m) => m.id === responseId);
        if (existingIndex !== -1) {
          const next = [...prev];
          next[existingIndex] = {
            ...next[existingIndex],
            text: initialText,
            thoughtSteps: mergeThoughtSteps(
              next[existingIndex].thoughtSteps,
              thoughtSteps,
            ),
          };
          return next;
        }

        // First tool call for this response — replace the previous response's last message
        // if its stage qualifies, otherwise append a new message.
        const last = prev[prev.length - 1];
        if (
          last?.role === 'assistant' &&
          last.isLivePatch &&
          replaceStages.has(stage)
        ) {
          return [
            ...prev.slice(0, -1),
            {
              ...last,
              id: responseId,
              text: initialText,
              thoughtSteps: mergeThoughtSteps(last.thoughtSteps, thoughtSteps),
            },
          ];
        }

        return [
          ...prev,
          {
            id: responseId,
            role: 'assistant',
            text: initialText,
            isLivePatch: true,
            thoughtSeconds: null,
            thoughtSteps: thoughtSteps,
          },
        ];
      });

      if (isFinalClass) {
        stopStreaming();
        // Split into word/whitespace tokens so each tick reveals complete words.
        const tokens = text.match(/\S+|\s+/g) || [text];
        let tokenIndex = 0;
        const TOKENS_PER_TICK = 3;
        const TICK_MS = 18;

        streamingIntervalRef.current = window.setInterval(() => {
          tokenIndex = Math.min(tokenIndex + TOKENS_PER_TICK, tokens.length);
          const streamed = tokens.slice(0, tokenIndex).join('');

          setMessages((prev) =>
            prev.map((m) =>
              m.id === responseId ? { ...m, text: streamed } : m,
            ),
          );

          if (tokenIndex >= tokens.length) {
            stopStreaming();
          }
        }, TICK_MS);
      }
    },
    [],
  );

  const attachThoughtSummary = useCallback(
    (responseId: string, patch: HtsUiPatch) => {
      const startedAt = thinkingStartedAtRef.current;
      const elapsedSeconds = startedAt
        ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000))
        : 0;
      const thoughtSteps = buildPatchThoughtSteps(patch);

      setMessages((prev) =>
        prev.map((message) =>
          message.id === responseId
            ? {
                ...message,
                thoughtSeconds: elapsedSeconds,
                thoughtSteps:
                  thoughtSteps.length > 0
                    ? thoughtSteps
                    : HTS_BASE_THOUGHT_STEPS,
              }
            : message,
        ),
      );
    },
    [],
  );

  const appendObservedMessage = useCallback(
    (message: Omit<HtsObservedChatMessage, 'id' | 'createdAt'>) => {
      observedMessagesRef.current = [
        ...observedMessagesRef.current,
        {
          id: buildObservedMessageId(),
          createdAt: new Date().toISOString(),
          ...message,
          text: truncateText(message.text, MAX_OBSERVED_MESSAGE_LENGTH) || '',
        },
      ].slice(-MAX_OBSERVED_MESSAGES);
    },
    [],
  );

  const resetSaveSnapshot = useCallback(() => {
    productDescRef.current = null;
    observedMessagesRef.current = [];
    observedStreamItemsRef.current = [];
    finalReportSnapshotRef.current = null;
  }, []);

  const buildFinalReportSnapshot = useCallback(
    (
      latest: HtsUiPatch['latest'],
      streamItem: string,
    ): HtsFinalReportSnapshot => ({
      version: 1,
      source: 'publish_final_classification',
      threadId: threadIdRef.current,
      payload: {
        thread_id: threadIdRef.current,
        product_desc: truncateText(
          productDescRef.current,
          MAX_REPORT_TEXT_LENGTH,
        ),
        hts_code: latest?.hts_code,
        cmdt_desc: truncateNullableText(
          latest?.cmdt_desc,
          MAX_REPORT_TEXT_LENGTH,
        ),
        summary_text: truncateNullableText(
          latest?.summary_text,
          MAX_REPORT_TEXT_LENGTH,
        ),
        legal_basis_summary: truncateNullableText(
          latest?.legal_basis_summary,
          MAX_REPORT_TEXT_LENGTH,
        ),
        confidence_note: truncateNullableText(
          latest?.confidence_note,
          MAX_REPORT_TEXT_LENGTH,
        ),
        reasoning_trace: limitStringArray(latest?.reasoning_trace),
        facts_relied_on: limitStringArray(latest?.facts_relied_on),
        chapter_number: latest?.chapter_number,
        chapter_description: truncateNullableText(
          latest?.chapter_description,
          MAX_REPORT_TEXT_LENGTH,
        ),
      },
      generated: latest || {},
      streamItem: truncateText(streamItem, MAX_STREAM_ITEM_LENGTH) || '',
    }),
    [],
  );

  const getSaveSnapshot = useCallback(
    (): HtsClassificationSaveSnapshot => ({
      version: 1,
      source: 'hts-classification-managed-agent',
      threadId: threadIdRef.current,
      capturedAt: new Date().toISOString(),
      observed: {
        messages: [...observedMessagesRef.current],
        streamItems: [...observedStreamItemsRef.current],
      },
      finalReport: finalReportSnapshotRef.current
        ? {
            ...finalReportSnapshotRef.current,
            savedAt: new Date().toISOString(),
          }
        : null,
    }),
    [],
  );

  const processStateResponse = useCallback(
    async (threadId: string, responseId: string) => {
      const { data } = await pyApiClient.get<LiveStateResponse>(
        `/classify/hts/managed-agent/state/${threadId}`,
      );

      const streamItems = data?.stream_items || [];
      const latest = data?.latest || {};
      if (data?.workflow_stage) {
        setWorkflowStage(data.workflow_stage);
      }

      const newItems = streamItems.slice(lastSeenStreamCountRef.current);
      lastSeenStreamCountRef.current = streamItems.length;

      newItems.forEach((item, index) => {
        const isLast = index === newItems.length - 1;
        const itemResponseId = isLast ? responseId : `${responseId}_${index}`;
        const patch: HtsUiPatch = {
          stream_item: item,
          latest: isLast ? latest : {},
        };

        appendLivePatchMessage(patch, itemResponseId);
        if (onLivePatchRef.current) {
          onLivePatchRef.current({ stream_item: item, latest: latest });
        }

        observedStreamItemsRef.current = [
          ...observedStreamItemsRef.current,
          truncateText(item, MAX_STREAM_ITEM_LENGTH) || '',
        ].slice(-MAX_STREAM_ITEMS);

        const stage = isLast ? latest.stage || '' : '';
        const isFinal = stage === 'FINAL_CLASSIFICATION';
        appendObservedMessage({
          role: 'assistant',
          text: buildPatchDisplayText(patch) || item,
          source: isFinal ? 'finalize' : 'live_patch',
        });

        if (isFinal) {
          finalReportSnapshotRef.current = buildFinalReportSnapshot(
            latest,
            item,
          );
        }
      });

      if (newItems.length > 0) {
        attachThoughtSummary(responseId, {
          stream_item: newItems[newItems.length - 1],
          latest: latest,
        });
      }

      return data;
    },
    [
      appendLivePatchMessage,
      appendObservedMessage,
      attachThoughtSummary,
      buildFinalReportSnapshot,
      pyApiClient,
    ],
  );

  const pollTurnUntilDone = useCallback(
    async (threadId: string, responseId: string) => {
      const pollIntervalMs = 1000;
      const maxPolls = 300;

      for (let attempt = 0; attempt < maxPolls; attempt += 1) {
        const state = await processStateResponse(threadId, responseId);
        const turnStatus = state.turn_status || 'idle';

        if (turnStatus !== 'running') {
          if (turnStatus === 'error') {
            throw new Error(
              state.last_error || 'There was an error during classification.',
            );
          }
          return;
        }

        await new Promise((resolve) =>
          window.setTimeout(resolve, pollIntervalMs),
        );
      }

      throw new Error(
        'Classification is taking longer than expected. Please try again.',
      );
    },
    [processStateResponse],
  );

  const beginThinking = () => {
    setSending(true);
    setError(null);
    const startedAt = Date.now();
    thinkingStartedAtRef.current = startedAt;
    setThinkingStartedAt(startedAt);
    if (onResponseStartRef.current) {
      onResponseStartRef.current();
    }
  };

  const endThinking = () => {
    setSending(false);
    setThinkingStartedAt(null);
    thinkingStartedAtRef.current = null;
    if (onResponseEndRef.current) {
      onResponseEndRef.current();
    }
  };

  const rememberThreadId = useCallback(
    (threadId: string) => {
      setSavedConversations((current) => {
        const next = current.map((item) =>
          item.id === activeConversationId
            ? { ...item, threadId: threadId }
            : item,
        );
        writeStoredConversations(next);
        return next;
      });
    },
    [activeConversationId],
  );

  const startNewChat = useCallback(
    async (notifyParent = true) => {
      stopStreaming();
      threadIdRef.current = null;
      lastSeenStreamCountRef.current = 0;
      setError(null);
      setSending(false);
      setWorkflowStage(null);
      setThinkingStartedAt(null);
      thinkingStartedAtRef.current = null;
      setComposerValue('');
      setMessages([]);
      setExpandedThoughtIds(new Set());
      setHistoryOpen(false);
      setConversationTitle('New Classification');
      setActiveConversationId(createConversationId());
      lastSubmittedUserMessageRef.current = '';
      lastSubmittedFromFinalReviewPromptRef.current = false;
      resetSaveSnapshot();

      if (notifyParent && onNewChatRef.current) {
        onNewChatRef.current();
      }

      if (onThreadChangeRef.current) {
        onThreadChangeRef.current(null);
      }
    },
    [resetSaveSnapshot],
  );

  useImperativeHandle(
    ref,
    () => ({
      startNewChat: () => startNewChat(false),
      getSaveSnapshot: getSaveSnapshot,
    }),
    [getSaveSnapshot, startNewChat],
  );

  const handleResetChat = () => {
    startNewChat().catch((error: any) => {
      const detail = toErrorMessage(
        error?.message || error,
        'Unable to start a new classification chat.',
      );
      setError(detail);
      if (onErrorRef.current) {
        onErrorRef.current(detail);
      }
    });
  };

  const handleShowHistory = () => {
    setError(null);
    setSavedConversations(readStoredConversations());
    setHistoryOpen(true);
  };

  const handleHideHistory = () => {
    setHistoryOpen(false);
  };

  const handleLoadConversation = async (conversation: SavedConversation) => {
    try {
      setActiveConversationId(conversation.id);
      setConversationTitle(conversation.title);
      // Conversation text is never persisted locally (see SavedConversation), so the
      // custom transcript is reconstructed from the managed-agent stream state.
      setMessages([]);
      setExpandedThoughtIds(new Set());
      setHistoryOpen(false);
      lastSubmittedUserMessageRef.current = '';
      lastSubmittedFromFinalReviewPromptRef.current = false;
      resetSaveSnapshot();
      threadIdRef.current = conversation.threadId;
      lastSeenStreamCountRef.current = 0;
      setWorkflowStage(null);

      if (conversation.threadId) {
        beginThinking();
        const responseId = `assistant_history_${Date.now()}`;
        const state = await processStateResponse(
          conversation.threadId,
          responseId,
        );
        if (
          !state.workflow_stage &&
          state.stream_items.length === 0 &&
          !state.latest?.stage
        ) {
          throw new Error(
            'This chat session has expired. Please start a new chat.',
          );
        }
      }
      if (onThreadChangeRef.current) {
        onThreadChangeRef.current(conversation.threadId);
      }
    } catch (err: any) {
      const detail = toErrorMessage(
        err?.message || err,
        'Unable to load chat history.',
      );
      setError(detail);
      if (onErrorRef.current) {
        onErrorRef.current(detail);
      }
    } finally {
      if (thinkingStartedAtRef.current) {
        endThinking();
      }
    }
  };

  const sendMessage = async (rawText: string) => {
    const text = rawText.trim();
    const isFinalized = workflowStage
      ? TERMINAL_WORKFLOW_STAGES.has(workflowStage)
      : false;
    if (!text || sending || isFinalized) {
      return;
    }

    setComposerValue('');
    setError(null);
    lastSubmittedUserMessageRef.current = text;
    lastSubmittedFromFinalReviewPromptRef.current = isFinalReviewMenuText(
      [...messages].reverse().find((message) => message.role === 'assistant')
        ?.text,
    );
    setConversationTitle((currentTitle) =>
      currentTitle === 'New Classification'
        ? buildConversationTitle(text)
        : currentTitle,
    );
    const optimisticMessageId = `user_${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: optimisticMessageId,
        role: 'user',
        text: text,
      },
    ]);
    appendObservedMessage({ role: 'user', text: text, source: 'composer' });

    beginThinking();
    const responseId = `assistant_${Date.now()}`;
    let messageAccepted = false;

    try {
      if (!threadIdRef.current) {
        if (!productDescRef.current) {
          productDescRef.current = text;
        }

        const { data } = await pyApiClient.post<StartSessionResponse>(
          '/classify/hts/managed-agent/session',
          { product_desc: text },
        );

        messageAccepted = true;
        threadIdRef.current = data.thread_id;
        lastSeenStreamCountRef.current = 0;
        setWorkflowStage(data.workflow_stage);
        rememberThreadId(data.thread_id);
        if (onThreadChangeRef.current) {
          onThreadChangeRef.current(data.thread_id);
        }

        await pollTurnUntilDone(data.thread_id, responseId);
      } else {
        const { data } = await pyApiClient.post<SendMessageResponse>(
          '/classify/hts/managed-agent/message',
          { thread_id: threadIdRef.current, message: text },
        );

        messageAccepted = true;
        setWorkflowStage(data.workflow_stage);
        await pollTurnUntilDone(threadIdRef.current, responseId);
      }
    } catch (requestError: any) {
      if (!messageAccepted) {
        setMessages((prev) =>
          prev.filter((message) => message.id !== optimisticMessageId),
        );
      }
      setComposerValue(text);
      const status = requestError?.response?.status;
      const detail =
        status === 404
          ? 'This chat session has expired. Please start a new chat.'
          : toErrorMessage(
              requestError?.response?.data?.detail ||
                requestError?.response?.data?.message ||
                requestError?.message,
              'Unable to send message. Please try again.',
            );
      setError(detail);
      if (onErrorRef.current) {
        onErrorRef.current(detail);
      }
    } finally {
      endThinking();
    }
  };

  const handleCustomSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    await sendMessage(composerValue);
  };

  const toggleThoughtDetails = (messageId: string) => {
    setExpandedThoughtIds((current) => {
      const next = new Set(current);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  };

  const handleComposerKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }

    event.preventDefault();
    handleCustomSubmit();
  };

  const groupedHistory = groupConversationsByAge(savedConversations);
  const isFinalized = workflowStage
    ? TERMINAL_WORKFLOW_STAGES.has(workflowStage)
    : false;
  const composerDisabled = sending || isFinalized;
  const isEmptyComposerState =
    !historyOpen && messages.length === 0 && !sending;

  return (
    <div className='bite-chatkit-shell'>
      {showToolbar && !isEmptyComposerState && (
        <div className='bite-chatkit-toolbar'>
          <div className='bite-chatkit-status'>
            {error ? (
              <span className='bite-chatkit-status-error'>
                Chat unavailable
              </span>
            ) : sending ? (
              <span className='bite-chatkit-status-thinking'>
                Agent responding…
              </span>
            ) : (
              <span className='bite-chatkit-status-ready'>Chat ready</span>
            )}
          </div>

          <button
            type='button'
            className='bite-chatkit-reset-btn'
            onClick={handleResetChat}
          >
            Reset Chat
          </button>
        </div>
      )}

      <div
        ref={chatRootRef}
        className='bite-chatkit-react bite-hts-react-chat'
        data-empty-state={isEmptyComposerState ? 'true' : 'false'}
      >
        <div className='bite-chatkit-threadbar'>
          {historyOpen ? (
            <button
              type='button'
              className='bite-chatkit-history-back'
              onClick={handleHideHistory}
            >
              <ArrowBackIcon sx={{ fontSize: 21 }} />
              <span>Chat history</span>
            </button>
          ) : (
            <div className='bite-chatkit-thread-title'>{conversationTitle}</div>
          )}
          <div className='bite-chatkit-thread-actions'>
            <button
              type='button'
              className='bite-chatkit-icon-btn'
              onClick={handleResetChat}
              disabled={sending}
              aria-label='Start new conversation'
              title='Start new conversation'
            >
              <EditOutlinedIcon sx={{ fontSize: 21 }} />
            </button>
            {SHOW_HISTORY_ICON && (
              <button
                type='button'
                className='bite-chatkit-icon-btn'
                onClick={handleShowHistory}
                disabled={sending}
                aria-label='Show chat history'
                title='Show chat history'
                data-active={historyOpen ? 'true' : undefined}
              >
                <HistoryOutlinedIcon sx={{ fontSize: 23 }} />
              </button>
            )}
          </div>
        </div>
        <div className='bite-chatkit-messages' ref={scrollRef}>
          {historyOpen ? (
            <div className='bite-chatkit-history-view'>
              <button
                type='button'
                className='bite-chatkit-history-new'
                onClick={handleResetChat}
                disabled={sending}
              >
                <EditOutlinedIcon sx={{ fontSize: 18 }} />
                New chat
              </button>

              {groupedHistory.length === 0 ? (
                <div className='bite-chatkit-history-empty'>
                  No chat history yet.
                </div>
              ) : (
                groupedHistory.map((group) => (
                  <div key={group.label} className='bite-chatkit-history-group'>
                    <div className='bite-chatkit-history-group-label'>
                      {group.label}
                    </div>
                    <div className='bite-chatkit-history-items'>
                      {group.items.map((item) => (
                        <button
                          key={item.id}
                          type='button'
                          className='bite-chatkit-history-item'
                          onClick={() => handleLoadConversation(item)}
                          data-active={
                            item.id === activeConversationId
                              ? 'true'
                              : undefined
                          }
                        >
                          {item.title}
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : null}

          {!historyOpen &&
            messages.map((message) => {
              const thoughtExpanded = expandedThoughtIds.has(message.id);
              const thoughtSteps =
                message.thoughtSteps && message.thoughtSteps.length > 0
                  ? message.thoughtSteps
                  : HTS_BASE_THOUGHT_STEPS;

              return (
                <div
                  key={message.id}
                  className={`bite-chatkit-message bite-chatkit-message-${message.role}`}
                >
                  {message.role === 'assistant' &&
                    message.thoughtSeconds !== undefined && (
                      <div className='bite-chatkit-thought-block'>
                        <button
                          type='button'
                          className='bite-chatkit-thought-summary'
                          onClick={() => toggleThoughtDetails(message.id)}
                          aria-expanded={thoughtExpanded}
                        >
                          {formatThoughtDuration(message.thoughtSeconds)}
                          <span
                            className='bite-chatkit-thought-chevron'
                            aria-hidden='true'
                          >
                            &gt;
                          </span>
                        </button>

                        {thoughtExpanded && (
                          <div className='bite-chatkit-thought-details'>
                            {thoughtSteps.map((step) => (
                              <div
                                key={step.label}
                                className='bite-chatkit-thought-step'
                              >
                                <span aria-hidden='true' />
                                <div>
                                  <strong>{step.label}</strong>
                                  {step.detail && <p>{step.detail}</p>}
                                </div>
                              </div>
                            ))}
                            <div className='bite-chatkit-thought-step bite-chatkit-thought-step-done'>
                              <span aria-hidden='true' />
                              <div>Done</div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  <div className='bite-chatkit-message-text'>
                    <ReactMarkdown>{message.text}</ReactMarkdown>
                  </div>
                </div>
              );
            })}

          {!historyOpen && sending && (
            <div className='bite-chatkit-thinking-wrap'>
              <div className='bite-chatkit-thought-summary'>
                {formatThoughtDuration(thinkingElapsedSeconds)}
                <span
                  className='bite-chatkit-thought-chevron'
                  aria-hidden='true'
                >
                  &gt;
                </span>
              </div>
              <div className='bite-chatkit-thinking-card'>
                <div className='bite-chatkit-thinking-title'>
                  <span aria-hidden='true' />
                  Thinking
                </div>
                <div className='bite-chatkit-thinking-step'>
                  {
                    HTS_THINKING_STEPS[
                      Math.floor(thinkingElapsedSeconds / 3) %
                        HTS_THINKING_STEPS.length
                    ]
                  }
                </div>
              </div>
            </div>
          )}
        </div>

        <form
          ref={composerFormRef}
          className='bite-chatkit-custom-composer'
          onSubmit={handleCustomSubmit}
        >
          <div className='bite-chatkit-custom-input-wrap'>
            <textarea
              ref={composerInputRef}
              id='hts-product-description'
              className='bite-chatkit-custom-input'
              value={composerValue}
              onChange={(event) => setComposerValue(event.target.value)}
              onKeyDown={handleComposerKeyDown}
              placeholder='Describe your products make up and purpose in detail...'
              aria-label='Describe your product'
              rows={1}
              disabled={composerDisabled}
              autoFocus
            />
            <button
              type='submit'
              className='bite-chatkit-custom-send'
              disabled={!composerValue.trim() || composerDisabled}
              aria-label='Send message'
            >
              <SendIcon sx={{ fontSize: 18 }} />
            </button>
          </div>
        </form>
      </div>

      {error && <div className='bite-chatkit-error-banner'>{error}</div>}
    </div>
  );
};

const ForwardedHtsClassificationChat = React.forwardRef(HtsClassificationChat);
ForwardedHtsClassificationChat.displayName = 'HtsClassificationChat';

export default ForwardedHtsClassificationChat;
